import React, { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLBadge } from "@/components/Badges";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, Bell, Lock, ChevronLeft, ChevronRight, List, CalendarDays } from "lucide-react";

const IMP = {
  HIGH: "border-red-500/40 text-red-400 bg-red-500/[0.06]",
  MEDIUM: "border-amber-500/40 text-amber-400 bg-amber-500/[0.06]",
  LOW: "border-white/15 text-zinc-400 bg-white/[0.03]",
};
const DOT = { HIGH: "bg-red-400", MEDIUM: "bg-amber-400", LOW: "bg-zinc-500" };
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function gcalLink(ev) {
  const d = (ev.date || "").replace(/-/g, "");
  const text = encodeURIComponent("OnchainLyfe: " + ev.title);
  const details = encodeURIComponent(ev.description || "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${d}/${d}&details=${details}`;
}
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function EventRow({ ev, alerts, toggleAlert }) {
  return (
    <div data-testid={`calendar-event-${ev.id}`} className="cl-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" style={{ transform: "none" }}>
      <div className="sm:w-28 shrink-0">
        <p className="font-mono-cl text-sm font-bold text-[#ccff00]">{new Date(ev.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
        <p className="font-mono-cl text-[11px] text-zinc-500">{ev.time}</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`font-mono-cl text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border ${IMP[ev.importance] || IMP.LOW}`}>{ev.importance}</span>
          <span className="font-mono-cl text-[10px] tracking-[0.12em] uppercase text-zinc-500">{ev.category}</span>
          {ev.region !== "global" && <span className="font-mono-cl text-[10px] uppercase text-zinc-500">{ev.region}</span>}
          {ev.tier === "alpha" && <CLBadge label="EXCLUSIVE" />}
        </div>
        <p className="font-semibold">{ev.title}</p>
        {ev.locked ? (
          <p className="text-xs text-[#ccff00] mt-1 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Alpha members see full catalyst notes</p>
        ) : (ev.description && <p className="text-sm text-zinc-400 mt-1">{ev.description}</p>)}
      </div>
      <div className="flex gap-2 shrink-0">
        <a href={gcalLink(ev)} target="_blank" rel="noreferrer" data-testid={`add-calendar-${ev.id}`}
          className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40 transition-colors" title="Add to Calendar">
          <CalendarPlus className="w-4 h-4" />
        </a>
        <button onClick={() => toggleAlert(ev.id)} data-testid={`alert-${ev.id}`}
          className={`grid place-items-center w-9 h-9 rounded-full border transition-colors ${alerts.includes(ev.id) ? "bg-[#ccff00] text-black border-[#ccff00]" : "border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40"}`} title="Set Alert">
          <Bell className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [alerts, setAlerts] = useState(() => JSON.parse(localStorage.getItem("cl_alerts") || "[]"));

  useEffect(() => { client.get("/calendar").then((r) => setEvents(r.data)).catch(() => {}); }, []);

  const toggleAlert = (id) => {
    const next = alerts.includes(id) ? alerts.filter((x) => x !== id) : [...alerts, id];
    setAlerts(next);
    localStorage.setItem("cl_alerts", JSON.stringify(next));
    toast.success(alerts.includes(id) ? "Alert removed" : "Alert set. We'll flag this catalyst for you.");
  };

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { const k = (e.date || "").slice(0, 10); (m[k] = m[k] || []).push(e); });
    return m;
  }, [events]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayKey = ymd(new Date());
  const selectedEvents = byDay[selected] || [];
  const upcoming = [...events].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono-cl text-[11px] tracking-[0.28em] text-[#ccff00] uppercase mb-2">Catalysts Ahead</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Alpha Calendar</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">FOMC, CPI, NFP, token unlocks, ETF decisions and regulatory deadlines. Add to your calendar or set an alert.</p>
        </div>
        <div className="flex items-center rounded-full border border-white/10 p-0.5 bg-black/40">
          {[["month", CalendarDays], ["list", List]].map(([k, Icon]) => (
            <button key={k} data-testid={`calendar-view-${k}`} onClick={() => setView(k)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${view === k ? "bg-[#ccff00] text-black" : "text-zinc-400 hover:text-white"}`}>
              <Icon className="w-3.5 h-3.5" /> {k === "month" ? "Month" : "List"}
            </button>
          ))}
        </div>
      </div>

      {view === "month" ? (
        <div className="mt-8 grid lg:grid-cols-[1.5fr_1fr] gap-8">
          <div className="cl-card p-5" style={{ transform: "none" }}>
            <div className="flex items-center justify-between mb-4">
              <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40"><ChevronLeft className="w-4 h-4" /></button>
              <p className="font-display text-xl font-bold">{monthLabel}</p>
              <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-zinc-300 hover:text-[#ccff00] hover:border-[#ccff00]/40"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WD.map((d) => <div key={d} className="text-center font-mono-cl text-[10px] tracking-[0.12em] uppercase text-zinc-600 py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((d, i) => {
                const key = ymd(d);
                const inMonth = d.getMonth() === cursor.getMonth();
                const dayEv = byDay[key] || [];
                const isToday = key === todayKey;
                const isSel = key === selected;
                return (
                  <button key={i} data-testid={`cal-day-${key}`} onClick={() => setSelected(key)}
                    className={`relative aspect-square rounded-lg p-1.5 flex flex-col items-center justify-start text-sm transition-colors border ${isSel ? "border-[#ccff00] bg-[#ccff00]/[0.08]" : "border-transparent hover:border-white/10"} ${inMonth ? "text-zinc-200" : "text-zinc-700"}`}>
                    <span className={`w-6 h-6 grid place-items-center rounded-full text-xs ${isToday ? "bg-[#ccff00] text-black font-bold" : ""}`}>{d.getDate()}</span>
                    {dayEv.length > 0 && (
                      <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                        {dayEv.slice(0, 3).map((e, j) => <span key={j} className={`w-1.5 h-1.5 rounded-full ${DOT[e.importance] || DOT.LOW}`} />)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-mono-cl text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-3">
              {new Date(selected).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </p>
            <div className="space-y-3">
              {selectedEvents.length === 0 && <p className="text-zinc-500 py-8 text-center cl-card" style={{ transform: "none" }}>No events on this day.</p>}
              {selectedEvents.map((ev) => <EventRow key={ev.id} ev={ev} alerts={alerts} toggleAlert={toggleAlert} />)}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {upcoming.length === 0 && <p className="text-zinc-500 py-12 text-center">No upcoming events.</p>}
          {upcoming.map((ev) => <EventRow key={ev.id} ev={ev} alerts={alerts} toggleAlert={toggleAlert} />)}
        </div>
      )}

      {!user && (
        <p className="text-sm text-zinc-500 mt-8">
          <Link to="/pricing" className="text-[#ccff00]">Join Alpha</Link> to unlock premium catalyst notes and personalized alerts.
        </p>
      )}
    </div>
  );
}
